import React, { FC, useMemo, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useMedia } from 'react-use';
import { v4 as uuid } from 'uuid';
import warningmotion from 'static/animations/warning-sign.json';
import { Divider } from 'components';
import { closeModal } from 'store/actionCreators/modal';
import Lottie from 'react-lottie';
import CloseButton from 'components/Buttons/CloseButton';
import { breakpoints } from 'theme';
import {
  CloseButtonWrapper,
  HeaderIcon,
  StyledAddMemberPopup,
  StyledAddMemberPopupWrapper,
} from '../../pages/CommunityOverallScheduler/CreateEventGroupPopup/styled';
import { SubscriptionPopupCard } from './styled';
import { useNavigate, useParams } from 'react-router-dom';
import { PROFILE_ROUTES, ROUTES } from 'configs';
import { doClearCurrentCommunityError } from 'store/actionCreators/currentCommunity';
import { getCommunities } from 'store/selectors/communities';
import { SubscriptionOverPopupVariant } from 'types/modal';

interface PopupSubscriptionOverProps {
  isOpen: boolean;
  variant?: SubscriptionOverPopupVariant;
}

const PopupSubscriptionOver: FC<PopupSubscriptionOverProps> = ({
  isOpen,
  variant = 'expired',
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobileView = useMedia(breakpoints.downSmPlus);
  const popupRef = useRef<HTMLDivElement>();
  const communities = useSelector(getCommunities);
  const { communityId } = useParams();
  const community = communities.find((c) => c.id === communityId);
  const navigate = useNavigate();
  const isNoActivePlan = variant === 'noActivePlan';
  const copyPrefix = isNoActivePlan
    ? 'subscriptions.subscriptionsPopup.noActivePlan'
    : 'subscriptions.subscriptionsPopup';

  const ownerName = `${community?.communityOwner?.firstName ?? ''} ${
    community?.communityOwner?.lastName ?? ''
  }`.trim();

  const handleDismiss = () => {
    if (!isNoActivePlan) {
      dispatch(doClearCurrentCommunityError());
      navigate(`${ROUTES.PROFILE}`, {
        replace: true,
      });
    }
    dispatch(closeModal());
  };

  const handleGoToSubscription = (e?: React.MouseEvent) => {
    e?.preventDefault();
    if (!isNoActivePlan) {
      dispatch(doClearCurrentCommunityError());
    }
    navigate(`${ROUTES.PROFILE}/${PROFILE_ROUTES.SUBSCRIPTION}`, {
      replace: true,
    });
    dispatch(closeModal());
  };

  const transComponents = {
    brPure: <br />,
    b: <b onClick={handleGoToSubscription}></b>,
  };

  const cards = useMemo(
    () => [
      {
        titleKey: `${copyPrefix}.firstBox.title`,
        subtitleKey: isNoActivePlan
          ? `${copyPrefix}.firstBox.subtitle`
          : undefined,
        btnTitle: t(`${copyPrefix}.firstBox.btnText`),
        onClick: handleGoToSubscription,
      },
      {
        titleKey: `${copyPrefix}.secondBox.title`,
        subtitleKey: isNoActivePlan
          ? `${copyPrefix}.secondBox.subtitle`
          : undefined,
        btnTitle: t(`${copyPrefix}.secondBox.btnText`),
        onClick: isNoActivePlan ? handleDismiss : handleGoToSubscription,
      },
    ],
    [copyPrefix, isNoActivePlan, t],
  );

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <AnimatePresence>
      {isOpen && (
        <StyledAddMemberPopupWrapper
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <StyledAddMemberPopup
            ref={popupRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <CloseButtonWrapper>
              <CloseButton hidden={false} onClick={handleDismiss} />
            </CloseButtonWrapper>
            <HeaderIcon info>
              <Lottie
                options={{
                  animationData: warningmotion,
                  loop: true,
                  autoPlay: true,
                }}
                width={isMobileView ? '140px' : '28vh'}
                height={isMobileView ? '140px' : '28vh'}
              />
            </HeaderIcon>
            <StyledAddMemberPopup.Header>
              <h2>{t(`${copyPrefix}.title`)}</h2>
              <p>
                <Trans
                  i18nKey={`${copyPrefix}.subtitle`}
                  values={{ ownerName }}
                  components={{
                    brPure: <br />,
                    b: <b onClick={handleGoToSubscription}></b>,
                  }}
                />
              </p>
            </StyledAddMemberPopup.Header>
            <Divider customMargin="20px 0px 10px 0px" />
            <StyledAddMemberPopup.CardWrapper>
              {cards.map((li) => (
                <SubscriptionPopupCard key={uuid()}>
                  <StyledAddMemberPopup.CardWrapper.Card.Header>
                    {li.subtitleKey ? (
                      <>
                        <StyledAddMemberPopup.CardWrapper.Card.title>
                          <Trans
                            i18nKey={li.titleKey}
                            values={{ ownerName }}
                            components={transComponents}
                          />
                        </StyledAddMemberPopup.CardWrapper.Card.title>
                        <StyledAddMemberPopup.CardWrapper.Card.subTitle>
                          <Trans
                            i18nKey={li.subtitleKey}
                            values={{ ownerName }}
                            components={transComponents}
                          />
                        </StyledAddMemberPopup.CardWrapper.Card.subTitle>
                      </>
                    ) : (
                      <StyledAddMemberPopup.CardWrapper.Card.subTitle>
                        <Trans
                          i18nKey={li.titleKey}
                          values={{ ownerName }}
                          components={transComponents}
                        />
                      </StyledAddMemberPopup.CardWrapper.Card.subTitle>
                    )}
                  </StyledAddMemberPopup.CardWrapper.Card.Header>
                  <StyledAddMemberPopup.CardWrapper.Card.BtnWrapper>
                    <StyledAddMemberPopup.CardWrapper.Card.Btn
                      onClick={li.onClick}
                    >
                      {li.btnTitle}
                    </StyledAddMemberPopup.CardWrapper.Card.Btn>
                  </StyledAddMemberPopup.CardWrapper.Card.BtnWrapper>
                </SubscriptionPopupCard>
              ))}
            </StyledAddMemberPopup.CardWrapper>
          </StyledAddMemberPopup>
        </StyledAddMemberPopupWrapper>
      )}
    </AnimatePresence>
  );
};

export default PopupSubscriptionOver;
